import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TableHeadCell } from "../../../shared/models/TableModels";
import type { LibraryMediaFilter } from "../utils/documentLibraryMediaUtils";

/**
 * Embedded User management tab on `/document-management`.
 * Set to `true` to restore Documents | Users top-level tabs and `DocumentManagementUsersTab`.
 */
export const DOCUMENT_MANAGEMENT_SHOW_USERS_TAB = false;

/** Max length for document summary on the Update document form. */
export const MAX_DOCUMENT_SUMMARY_LENGTH = 500;

export const DOCUMENT_CATEGORIES = ["", "diagnosis", "instruction"] as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  "": "(none)",
  diagnosis: "Diagnosis",
  instruction: "Instruction",
};

/** True when the user picked a real category (not the default “(none)”). */
export function isDocumentCategorySelected(
  category: string
): category is Exclude<DocumentCategory, ""> {
  return category.trim().length > 0;
}

export type DocumentLibrarySortProperty =
  | "original_file_name"
  | "dataset_name"
  | "upload_status"
  | "role_access"
  | "company_name"
  | "is_active";

export function documentLibrarySortPropertyType(
  property: string
): "string" | "number" {
  return property === "is_active" ? "number" : "string";
}

export function useDocumentLibraryHeaders(
  libraryMediaFilter: LibraryMediaFilter = "document"
) {
  const { t } = useTranslation();

  return useMemo(
    () =>
      [
        {
          id: "original_file_name",
          label:
            libraryMediaFilter === "video"
              ? t("opva.dmColVideo", { defaultValue: "Video" })
              : t("opva.dmColDocument", { defaultValue: "Document" }),
          width: 22,
          sorting: true,
          sortingProperty: "original_file_name",
          position: "left",
        },
        {
          id: "dataset_name",
          label: t("opva.dmColDataset", { defaultValue: "Dataset" }),
          width: 14,
          sorting: true,
          sortingProperty: "dataset_name",
          position: "left",
        },
        {
          id: "company_name",
          label: t("opva.dmColCompany", { defaultValue: "Company" }),
          width: 14,
          sorting: true,
          sortingProperty: "company_name",
          position: "left",
        },
        {
          id: "role_access",
          label: t("opva.dmColRoles", { defaultValue: "Roles" }),
          width: 16,
          sorting: true,
          sortingProperty: "role_access",
          position: "left",
        },
        {
          id: "upload_status",
          label: t("opva.dmColStatus", { defaultValue: "Status" }),
          width: 12,
          sorting: true,
          sortingProperty: "upload_status",
          position: "left",
        },
        {
          id: "is_active",
          label: t("opva.dmColActive", { defaultValue: "Availability" }),
          width: 10,
          sorting: true,
          sortingProperty: "is_active",
          position: "left",
        },
        {
          id: "actions",
          label: t("opva.actions"),
          width: 8,
          sorting: false,
          sortingProperty: "",
          position: "center",
        },
      ] as TableHeadCell[],
    [libraryMediaFilter, t]
  );
}
